import classNames from 'classnames';
import { type ComponentProps, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePageWidth } from '../../hooks/usePageWidth';
import CalendarIcon from '../../icons/Calendar';
import Clear from '../../icons/Clear';
import { InputBaseProps } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconButton from '../IconButton';
import InputField from '../InputField';
import PopupMenu from '../PopupMenu';
import Calendar from './Calendar/Calendar';
import styles from './styles/styles.module.scss';

type DatePickerProps = InputBaseProps &
  ComponentProps<typeof Calendar> & {
    className?: string;
    displayValueFormatter?: (date?: Date) => string;
    hideClearDate?: boolean;
    placeholder?: string;
    isFixedHeight?: boolean; // If true, the popup will have a fixed height
  };

const DatePicker = ({
  className,
  disabled,
  displayValueFormatter,
  errorText,
  helperText,
  hideClearDate,
  hideLabel,
  labelText,
  labelTextVariant,
  placeholder,
  isFixedHeight,
  ...calendarProps
}: DatePickerProps) => {
  const { languageRegionCode, mode, onSelect, selected } = calendarProps;
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const pageWidth = usePageWidth();
  const isMobile = pageWidth === 'xs';

  useEffect(() => {
    if (mode === 'single' && selected) {
      setIsOpen(false);
    } else if (mode === 'range' && selected?.to) {
      setIsOpen(false);
    }
    // Only want to call this when a new "selected" value is passed in
  }, [selected]);

  const displayValue = useMemo(() => {
    if (mode === 'single' && displayValueFormatter) {
      return displayValueFormatter(selected);
    }

    if (mode === 'single' && selected) {
      return selected.toLocaleDateString(languageRegionCode, { year: 'numeric', month: 'long', day: 'numeric' });
    }
    if (mode === 'range' && selected?.from && selected?.to) {
      return intl.formatDateTimeRange(selected.from, selected.to, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return '';
  }, [displayValueFormatter, intl, languageRegionCode, mode, selected]);

  const anchor = useMemo(() => {
    const showClearButton = !hideClearDate && selected && !disabled;
    return (
      <InputField
        className={classNames(className, styles.container)}
        disabled={disabled}
        errorText={errorText}
        helperText={helperText}
        labelText={hideLabel ? '' : labelText}
        labelTextVariant={labelTextVariant}
        useDivInsteadOfLabel
      >
        <div className={styles.buttonContainer}>
          <button
            className={classNames(styles.button, { [styles.active]: isOpen, [styles.hasError]: Boolean(errorText) })}
            aria-label={hideLabel ? labelText : undefined}
            disabled={disabled}
            onClick={() => {
              setIsOpen(true);
            }}
            type="button"
          >
            <span className={classNames(styles.displayValue, { [styles.placeholder]: !displayValue })}>{displayValue || placeholder}</span>
          </button>
          {showClearButton ? (
            <IconButton
              className={styles.clearButton}
              icon={{ Component: Clear, color: '--color-text-secondary' }}
              onClick={() => onSelect(undefined)}
              title={intl.formatMessage({ defaultMessage: 'Clear' })}
              variant="ghost"
            />
          ) : (
            <CalendarIcon className={styles.calendarIcon} color={disabled ? '--color-text-disabled' : '--color-text-secondary'} />
          )}
        </div>
      </InputField>
    );
  }, [
    className,
    disabled,
    displayValue,
    errorText,
    helperText,
    hideClearDate,
    hideLabel,
    intl,
    isOpen,
    labelText,
    labelTextVariant,
    mode,
    onSelect,
    placeholder,
    selected,
    setIsOpen
  ]);

  return (
    <PopupMenu
      {...setDenaliAttribute('DatePicker')}
      containerClassName={classNames(styles.popupContainer, { [styles.popupContainerFixed]: isFixedHeight })}
      headerAndContentClassName={styles.calendarContainer}
      anchor={anchor}
      hideCloseButton
      isOpen={isOpen}
      onCloseRequest={() => setIsOpen(false)}
      useBottomSheet={isMobile}
      usePortal
    >
      <Calendar {...calendarProps} />
    </PopupMenu>
  );
};

export default DatePicker;
